import { asciiDigitsOnly } from "@a2z/text";
import type { CustomerSuggestion } from "@/lib/api/types";

/**
 * T6 / F29 (D150) — «العميل بتاعنا»: the delivery dialog's one suggestion.
 *
 * The SERVER decides what matches (exact, normalised — `customer-suggestion
 * .policy.ts`); this file only decides WHEN to ask and WHAT one tap fills.
 */

/**
 * The typed number's digits, Arabic-Indic folded to ASCII, everything else
 * dropped. `@a2z/text`'s `asciiDigitsOnly` — the same folding the backend's
 * phone normaliser (`shared/phone/phone.util.ts`) and the stored
 * `customer_phone_tail` use, so the box and the server count the same digits.
 */
export function phoneDigits(raw: string): string {
  return asciiDigitsOnly(raw);
}

/** Ask once the number could be a whole Egyptian mobile — ten digits or more. */
export const SUGGESTION_MIN_DIGITS = 10;

export function shouldAskForSuggestion(raw: string): boolean {
  return phoneDigits(raw).length >= SUGGESTION_MIN_DIGITS;
}

/**
 * What «استخدمه» writes. NEVER over something already typed (the rider's and
 * the cashier's condition): only an EMPTY name, and on a delivery only an
 * EMPTY address, take the suggestion's value. A takeaway has no address box,
 * so it never receives one.
 */
export function applySuggestion(
  current: { customerName: string; deliveryAddress: string },
  suggestion: Pick<CustomerSuggestion, "customerName" | "deliveryAddress">,
  isDelivery: boolean,
): { customerName: string; deliveryAddress: string } {
  return {
    customerName:
      current.customerName.trim() === "" && suggestion.customerName
        ? suggestion.customerName
        : current.customerName,
    deliveryAddress:
      isDelivery &&
      current.deliveryAddress.trim() === "" &&
      suggestion.deliveryAddress
        ? suggestion.deliveryAddress
        : current.deliveryAddress,
  };
}

/** Whether the suggestion still has anything to give this form. */
export function suggestionIsUseful(
  current: { customerName: string; deliveryAddress: string },
  suggestion: Pick<CustomerSuggestion, "customerName" | "deliveryAddress">,
  isDelivery: boolean,
): boolean {
  const next = applySuggestion(current, suggestion, isDelivery);
  return (
    next.customerName !== current.customerName ||
    next.deliveryAddress !== current.deliveryAddress
  );
}
